import { useMemo, useState, useEffect } from 'react';
import { Empty, Spin, Modal, Tag, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import type { Task } from '@/types';

/**
 * Ma trận Eisenhower kết hợp biểu đồ bong bóng (5x5).
 *
 * Trục:
 * - X (ngang): Mức độ Khẩn cấp 1-5 (trái = 5 khẩn nhất, phải = 1)
 * - Y (dọc): Mức độ Quan trọng 1-5 (trên = 5 quan trọng nhất, dưới = 1)
 *
 * 4 quadrant — tên + subtitle lấy từ system settings:
 * - Q1 (top-left, urgent≥4 + important≥4): đỏ
 * - Q2 (top-right, urgent≤3 + important≥4): xanh lá
 * - Q4 (bottom-left, urgent≥4 + important≤3): vàng
 * - Q3 (bottom-right, urgent≤3 + important≤3): xanh dương
 *
 * Click bubble/quadrant → popup list task; click task → navigate detail.
 */
export function EisenhowerMatrix({
  tasks,
  loading,
  title = 'Ma trận Eisenhower',
}: {
  tasks: Task[];
  loading?: boolean;
  title?: string;
  onCellClick?: (quadrant: 'Q1' | 'Q2' | 'Q3' | 'Q4') => void;
  threshold?: number;
}) {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  const [popupState, setPopupState] = useState<{
    visible: boolean;
    tasks: Task[];
    title: string;
    color: string;
  } | null>(null);

  // Responsive
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handler = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024;

  // Group task theo (urgent, important)
  const { grid, counts, total } = useMemo(() => {
    const g: Record<number, Record<number, Task[]>> = {};
    for (let u = 1; u <= 5; u++) {
      g[u] = {};
      for (let i = 1; i <= 5; i++) g[u][i] = [];
    }
    const q = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
    for (const t of tasks) {
      if (t.status === 'COMPLETED' || t.status === 'CANCELLED') continue;
      const urg = (t as any).priorityLevel ?? 3;
      const imp = (t as any).importantLevel ?? 3;
      const u = Math.max(1, Math.min(5, urg));
      const i = Math.max(1, Math.min(5, imp));
      g[u][i].push(t);
      if (u >= 4 && i >= 4) q.Q1++;
      else if (u <= 3 && i >= 4) q.Q2++;
      else if (u >= 4 && i <= 3) q.Q4++;
      else q.Q3++;
    }
    return { grid: g, counts: q, total: q.Q1 + q.Q2 + q.Q3 + q.Q4 };
  }, [tasks]);

  if (loading) {
    return (
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, border: '1px solid #e8edf2', textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (total === 0) {
    return (
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, border: '1px solid #e8edf2' }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1a1e23', marginBottom: 16 }}>{title}</h3>
        <Empty description="Chưa có task active" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  // Max count để scale bubble
  let maxCount = 1;
  for (let u = 1; u <= 5; u++) {
    for (let i = 1; i <= 5; i++) {
      if (grid[u][i].length > maxCount) maxCount = grid[u][i].length;
    }
  }

  const bubbleSize = (count: number): number => {
    if (count === 0) return 0;
    const minSize = isMobile ? 16 : 22;
    const maxSize = isMobile ? 44 : isTablet ? 54 : 64;
    return minSize + Math.sqrt(count / maxCount) * (maxSize - minSize);
  };

  const cellQuadrant = (u: number, i: number): 'Q1' | 'Q2' | 'Q3' | 'Q4' => {
    if (u >= 4 && i >= 4) return 'Q1';
    if (u <= 3 && i >= 4) return 'Q2';
    if (u >= 4 && i <= 3) return 'Q4';
    return 'Q3';
  };

  const QC = {
    Q1: { bubble: '#ef4444', bubbleHover: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
    Q2: { bubble: '#10b981', bubbleHover: '#059669', bg: '#f0fdf4', border: '#bbf7d0' },
    Q3: { bubble: '#3b82f6', bubbleHover: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
    Q4: { bubble: '#f59e0b', bubbleHover: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  };

  // V1.0.29: lấy labels từ system settings (admin config được)
  const QI = {
    Q1: { title: settings.eis_q1_title ?? 'Q1', subtitle: settings.eis_q1_subtitle ?? '', short: settings.eis_q1_short ?? 'Q1' },
    Q2: { title: settings.eis_q2_title ?? 'Q2', subtitle: settings.eis_q2_subtitle ?? '', short: settings.eis_q2_short ?? 'Q2' },
    Q3: { title: settings.eis_q3_title ?? 'Q3', subtitle: settings.eis_q3_subtitle ?? '', short: settings.eis_q3_short ?? 'Q3' },
    Q4: { title: settings.eis_q4_title ?? 'Q4', subtitle: settings.eis_q4_subtitle ?? '', short: settings.eis_q4_short ?? 'Q4' },
  };
  const axisX = settings.eis_axis_x ?? 'MỨC ĐỘ KHẨN CẤP';
  const axisY = settings.eis_axis_y ?? 'MỨC ĐỘ QUAN TRỌNG';

  const handleBubbleClick = (u: number, i: number) => {
    const cellTasks = grid[u][i];
    if (cellTasks.length === 0) return;
    const q = cellQuadrant(u, i);
    setPopupState({
      visible: true,
      tasks: cellTasks,
      title: `Task tại Khẩn cấp ${u} · Quan trọng ${i} (${QI[q].short})`,
      color: QC[q].bubble,
    });
  };

  const handleQuadrantClick = (q: 'Q1' | 'Q2' | 'Q3' | 'Q4') => {
    const allTasks: Task[] = [];
    for (let u = 1; u <= 5; u++) {
      for (let i = 1; i <= 5; i++) {
        if (cellQuadrant(u, i) === q) allTasks.push(...grid[u][i]);
      }
    }
    if (allTasks.length === 0) return;
    setPopupState({ visible: true, tasks: allTasks, title: QI[q].title, color: QC[q].bubble });
  };

  const xLabels = [5, 4, 3, 2, 1];
  const yLabels = [5, 4, 3, 2, 1];

  // Tham số layout
  const yLabelWidth = isMobile ? 32 : 60;
  const yAxisVectorWidth = isMobile ? 28 : 36;
  const cellHeight = isMobile ? 50 : 70;

  return (
    <div style={{
      background: '#fff', borderRadius: 8,
      padding: isMobile ? 10 : 16,
      border: '1px solid #e8edf2',
    }}>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 4 : 0,
        marginBottom: 10,
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1a1e23' }}>{title}</h3>
          <div style={{ fontSize: 11, color: '#7d8a98', fontStyle: 'italic', marginTop: 2 }}>
            Kích thước bong bóng thể hiện số lượng task — click bong bóng hoặc Q1-Q4 để xem chi tiết
          </div>
        </div>
        <span style={{ fontSize: 11, color: '#7d8a98' }}>
          Tổng: <b style={{ color: '#1a1e23', fontSize: 13 }}>{total}</b> task
        </span>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 12,
        alignItems: 'flex-start',
      }}>
        {/* Matrix container */}
        <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
          {/* X axis arrow vector (trên matrix) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `${yAxisVectorWidth}px ${yLabelWidth}px 1fr`,
            gap: 0, marginBottom: 4,
          }}>
            <div /> {/* corner */}
            <div /> {/* y label col */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                fontSize: isMobile ? 10 : 12,
                fontWeight: 700, color: '#1f2937',
                letterSpacing: 0.5,
                whiteSpace: 'nowrap',
              }}>
                {axisX}
              </span>
              {/* SVG arrow ngang */}
              <svg
                height={isMobile ? 12 : 14}
                style={{ flex: 1, minWidth: 0 }}
                preserveAspectRatio="none"
                viewBox="0 0 100 14"
              >
                <defs>
                  <marker id="arrowX" markerWidth="10" markerHeight="10"
                    refX="8" refY="5" orient="auto">
                    <path d="M0,0 L8,5 L0,10 z" fill="#1f2937" />
                  </marker>
                </defs>
                <line x1="0" y1="7" x2="92" y2="7"
                  stroke="#1f2937" strokeWidth="1.5"
                  markerEnd="url(#arrowX)" />
              </svg>
            </div>
          </div>

          {/* X axis numbers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `${yAxisVectorWidth}px ${yLabelWidth}px repeat(5, 1fr)`,
            gap: 0, marginBottom: 4,
          }}>
            <div />
            <div />
            {xLabels.map(n => (
              <div key={n} style={{
                textAlign: 'center', padding: '2px 0',
                color: n >= 4 ? '#dc2626' : '#16a34a',
              }}>
                <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 700 }}>{n}</div>
                {!isMobile && (
                  <div style={{ fontSize: 9, fontWeight: 500 }}>
                    {n >= 4 ? 'Khẩn cấp' : 'Ít khẩn cấp'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Main matrix: Y axis arrow + Y labels + 5x5 grid */}
          <div style={{ display: 'flex', gap: 0 }}>
            {/* Y axis vector arrow (dọc) */}
            <div style={{
              width: yAxisVectorWidth,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <span style={{
                writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                fontSize: isMobile ? 10 : 12, fontWeight: 700, color: '#1f2937',
                letterSpacing: 0.5, whiteSpace: 'nowrap',
                paddingBottom: 4,
              }}>
                {axisY}
              </span>
              <svg
                width={isMobile ? 12 : 14}
                style={{ flex: 1, minHeight: 0 }}
                preserveAspectRatio="none"
                viewBox="0 0 14 100"
              >
                <defs>
                  <marker id="arrowY" markerWidth="10" markerHeight="10"
                    refX="5" refY="8" orient="auto">
                    <path d="M0,0 L10,0 L5,8 z" fill="#1f2937" />
                  </marker>
                </defs>
                <line x1="7" y1="0" x2="7" y2="92"
                  stroke="#1f2937" strokeWidth="1.5"
                  markerEnd="url(#arrowY)" />
              </svg>
            </div>

            {/* Y labels + matrix grid */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {yLabels.map(i => (
                <div key={i} style={{
                  display: 'grid',
                  gridTemplateColumns: `${yLabelWidth}px repeat(5, 1fr)`,
                  gap: 0, height: cellHeight,
                }}>
                  {/* Y label cell */}
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: i >= 4 ? '#dc2626' : '#2563eb',
                    paddingRight: 4,
                  }}>
                    <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 700 }}>{i}</div>
                    {!isMobile && (
                      <div style={{ fontSize: 9, fontWeight: 500, textAlign: 'center', lineHeight: 1.1 }}>
                        {i === 5 ? 'Rất quan trọng' : i >= 4 ? 'Quan trọng' : 'Ít quan trọng'}
                      </div>
                    )}
                  </div>

                  {/* 5 cells */}
                  {xLabels.map(u => {
                    const q = cellQuadrant(u, i);
                    const c = QC[q];
                    const cellTasks = grid[u][i];
                    const count = cellTasks.length;
                    const size = bubbleSize(count);
                    const heavyBottom = i === 4;
                    const heavyRight = u === 4;
                    return (
                      <div key={`${u}-${i}`} style={{
                        background: c.bg,
                        border: `1px solid ${c.border}`,
                        borderBottomWidth: heavyBottom ? 2 : 1,
                        borderBottomColor: heavyBottom ? '#64748b' : c.border,
                        borderRightWidth: heavyRight ? 2 : 1,
                        borderRightColor: heavyRight ? '#64748b' : c.border,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {count > 0 && (
                          <Tooltip title={`Khẩn ${u} · Quan trọng ${i} — ${count} task`}>
                            <div
                              onClick={() => handleBubbleClick(u, i)}
                              style={{
                                width: size, height: size, borderRadius: '50%',
                                background: c.bubble, color: '#fff',
                                fontSize: size >= 38 ? 15 : 11, fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                transition: 'transform 0.15s, box-shadow 0.15s',
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.12)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                              }}
                            >
                              {count}
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel: chỉ giữ TỔNG HỢP + TỔNG CỘNG (bỏ Gợi ý theo yêu cầu) */}
        <div style={{
          width: isMobile ? '100%' : 200,
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: 8,
        }}>
          <div style={{
            background: '#f8fafc', borderRadius: 6, padding: 10,
            border: '1px solid #e5e7eb',
            flex: isMobile ? '1 1 calc(60% - 4px)' : 'initial',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: '#1f2937', fontSize: 11 }}>
              TỔNG HỢP SỐ LƯỢNG TASK
            </div>
            {(['Q1', 'Q2', 'Q3', 'Q4'] as const).map(q => {
              const c = QC[q];
              const info = QI[q];
              return (
                <div
                  key={q}
                  onClick={() => handleQuadrantClick(q)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '5px 6px', marginBottom: 2,
                    cursor: counts[q] > 0 ? 'pointer' : 'default',
                    borderRadius: 4,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => {
                    if (counts[q] > 0) e.currentTarget.style.background = '#e2e8f0';
                  }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{
                    width: 11, height: 11, borderRadius: '50%',
                    background: c.bubble, flexShrink: 0,
                  }} />
                  <span style={{ flex: 1, fontSize: 10, color: '#374151', lineHeight: 1.3 }}>
                    {q} – {info.short}:
                  </span>
                  <b style={{ color: c.bubble, fontSize: 12 }}>{counts[q]}</b>
                </div>
              );
            })}
          </div>

          <div style={{
            background: '#f0f9ff', borderRadius: 6, padding: 10,
            border: '1px solid #bae6fd',
            textAlign: 'center',
            flex: isMobile ? '1 1 calc(40% - 4px)' : 'initial',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ fontSize: 10, color: '#075985', fontWeight: 600, letterSpacing: 0.5 }}>TỔNG CỘNG</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0c4a6e' }}>{total} task</div>
          </div>
        </div>
      </div>

      {/* Popup */}
      <Modal
        open={!!popupState?.visible}
        onCancel={() => setPopupState(null)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: popupState?.color,
            }} />
            <span style={{ fontSize: 14 }}>{popupState?.title}</span>
            <Tag color="blue">{popupState?.tasks.length ?? 0} task</Tag>
          </div>
        }
        footer={null}
        width={isMobile ? '95vw' : 720}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {popupState?.tasks.map(t => {
            const urg = (t as any).priorityLevel ?? 3;
            const imp = (t as any).importantLevel ?? 3;
            const isOverdue = t.dueDate && dayjs(t.dueDate).isBefore(dayjs(), 'day') && t.status !== 'COMPLETED';
            return (
              <div
                key={t.id}
                onClick={() => {
                  navigate(`/tasks/${t.id}`);
                  setPopupState(null);
                }}
                style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, marginBottom: 4,
                      display: 'flex', gap: 8, flexWrap: 'wrap',
                    }}>
                      <span style={{ color: urg >= 4 ? '#cf1322' : urg >= 3 ? '#fa8c16' : '#7d8a98' }}>
                        🔴 Gấp: Cấp {urg}
                      </span>
                      <span style={{ color: '#bdc4cd' }}>·</span>
                      <span style={{ color: imp >= 4 ? '#d46b08' : imp >= 3 ? '#faad14' : '#7d8a98' }}>
                        🟠 Quan trọng: Cấp {imp}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1e23', marginBottom: 4 }}>
                      {t.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#5b6875', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: '#1976D2' }}>{t.code}</span>
                      <span>·</span>
                      <Tag style={{ fontSize: 10, padding: '0 6px', lineHeight: '18px', margin: 0 }}>
                        {t.status}
                      </Tag>
                      {t.dueDate && (
                        <>
                          <span>·</span>
                          <span style={{ color: isOverdue ? '#cf1322' : '#5b6875', fontWeight: isOverdue ? 600 : 400 }}>
                            📅 {dayjs(t.dueDate).format('DD/MM/YYYY')}
                            {isOverdue && ' (Quá hạn)'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: 16, color: '#1976D2' }}>→</span>
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
